import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Upload, Info, X, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LocationAutofill from '../components/LocationAutofill';
import { useListingStore } from '../stores/listingStore';
import { sanitizeDescription } from '../utils/contactFilter';

interface ImagePreview {
  file: File;
  preview: string;
}

interface Coordinates {
  lat: number;
  lng: number;
}

const AddListing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { addListing } = useListingStore();
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>([]);
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    exactAddress: '',
    displayAddress: '',
    location: '',
    price: '',
    bedrooms: '1',
    bathrooms: '1',
    sqft: '',
    availableDate: '',
    endDate: '',
    amenities: [] as string[],
    applicantCriteria: {
      minimumIncome: '',
      minimumCreditScore: '600',
      requireBackgroundCheck: true,
      requireReferences: false,
      referencesCount: '2',
      employmentVerification: true,
      rentalHistory: true,
      preferredOccupations: [] as string[],
      additionalRequirements: ''
    }
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPreviews: ImagePreview[] = [];
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push({
          file,
          preview: reader.result as string
        });
        setImagePreviews([...imagePreviews, ...newPreviews]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated || !user) {
      setShowSignupPrompt(true);
      return;
    }

    // Sanitize description to remove contact information
    const sanitizedDescription = sanitizeDescription(formData.description);

    // Calculate end date if not provided (default to 6 months from available date)
    const availableDate = new Date(formData.availableDate);
    const endDate = formData.endDate || (() => {
      const date = new Date(availableDate);
      date.setMonth(date.getMonth() + 6);
      return date.toISOString().split('T')[0];
    })();

    // Create new listing
    const newListing = {
      id: Date.now().toString(),
      ownerId: user.id,
      title: formData.title,
      description: sanitizedDescription,
      location: formData.location,
      displayLocation: formData.displayAddress,
      exactAddress: formData.exactAddress,
      price: parseInt(formData.price),
      beds: parseInt(formData.bedrooms),
      baths: parseFloat(formData.bathrooms),
      sqft: parseInt(formData.sqft),
      available: formData.availableDate,
      endDate,
      status: 'active' as const,
      images: imagePreviews.map(img => img.preview),
      amenities: formData.amenities,
      coordinates: coordinates || {
        lat: 40.7128,
        lng: -74.0060
      },
      applicantCriteria: formData.applicantCriteria,
      isLeased: false
    };

    // Add listing to store
    addListing(newListing);

    // Navigate to dashboard
    navigate('/dashboard');
  };

  const toggleAmenity = (amenityId: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(id => id !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const amenities = [
    'airConditioning',
    'heating',
    'washerDryer',
    'dishwasher',
    'internet',
    'tv',
    'parking',
    'gym',
    'pool',
    'elevator',
    'doorman',
    'furnished'
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-2xl font-bold mb-8">List Your Place</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                Note: Contact information will be automatically removed for privacy and security.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <LocationAutofill
                  value={formData.location}
                  onChange={(value) => setFormData({ ...formData, location: value })}
                  onSelect={(location) => {
                    setFormData({ 
                      ...formData, 
                      exactAddress: location.exactAddress || location.name,
                      displayAddress: location.area || location.city || '',
                      location: `${location.city}, ${location.state}`
                    });
                    if (location.coordinates) {
                      setCoordinates(location.coordinates);
                    }
                  }}
                />
                <p className="mt-1 text-sm text-gray-500">
                  This will only be shown to approved applicants
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Location
                </label>
                <input
                  type="text"
                  value={formData.displayAddress}
                  onChange={(e) => setFormData({ ...formData, displayAddress: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Upper East Side"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  This will be shown publicly
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Rent
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bedrooms
                </label>
                <select
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="0">Studio</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bathrooms
                </label>
                <select
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="1">1</option>
                  <option value="1.5">1.5</option>
                  <option value="2">2</option>
                  <option value="2.5">2.5</option>
                  <option value="3">3+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Square Feet
                </label>
                <input
                  type="number"
                  value={formData.sqft}
                  onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available From
                </label>
                <input
                  type="date"
                  value={formData.availableDate}
                  onChange={(e) => setFormData({ ...formData, availableDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available Until (Optional)
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  min={formData.availableDate}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Defaults to 6 months if not specified
                </p>
              </div>
            </div>

            {/* Amenities */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {amenities.map((amenity) => (
                  <label key={amenity} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.amenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-gray-700">
                      {amenity.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Applicant Requirements */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Applicant Requirements</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Monthly Income
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        value={formData.applicantCriteria.minimumIncome}
                        onChange={(e) => setFormData({
                          ...formData,
                          applicantCriteria: {
                            ...formData.applicantCriteria,
                            minimumIncome: e.target.value
                          }
                        })}
                        placeholder="e.g., 4000"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Recommended: 3x monthly rent (${parseInt(formData.price || '0') * 3})
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Credit Score
                    </label>
                    <select
                      value={formData.applicantCriteria.minimumCreditScore}
                      onChange={(e) => setFormData({
                        ...formData,
                        applicantCriteria: {
                          ...formData.applicantCriteria,
                          minimumCreditScore: e.target.value
                        }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="none">No Minimum</option>
                      <option value="550">550+</option>
                      <option value="600">600+</option>
                      <option value="650">650+</option>
                      <option value="700">700+</option>
                      <option value="750">750+</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">Background Check</h3>
                      <p className="text-sm text-gray-500">Require criminal background check</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.applicantCriteria.requireBackgroundCheck}
                        onChange={(e) => setFormData({
                          ...formData,
                          applicantCriteria: {
                            ...formData.applicantCriteria,
                            requireBackgroundCheck: e.target.checked
                          }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">Employment Verification</h3>
                      <p className="text-sm text-gray-500">Verify current employment status</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.applicantCriteria.employmentVerification}
                        onChange={(e) => setFormData({
                          ...formData,
                          applicantCriteria: {
                            ...formData.applicantCriteria,
                            employmentVerification: e.target.checked
                          }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">Rental History</h3>
                      <p className="text-sm text-gray-500">Require previous rental history</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.applicantCriteria.rentalHistory}
                        onChange={(e) => setFormData({
                          ...formData,
                          applicantCriteria: {
                            ...formData.applicantCriteria,
                            rentalHistory: e.target.checked
                          }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Requirements (Optional)
                  </label>
                  <textarea
                    value={formData.applicantCriteria.additionalRequirements}
                    onChange={(e) => setFormData({
                      ...formData,
                      applicantCriteria: {
                        ...formData.applicantCriteria,
                        additionalRequirements: e.target.value
                      }
                    })}
                    rows={3}
                    placeholder="Any other specific requirements or preferences..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Photo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Photos
              </label>
              <div className="mt-1 flex flex-col space-y-4">
                {imagePreviews.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {imagePreviews.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.preview}
                          alt={`Preview ${index + 1}`}
                          className="h-40 w-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="images" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                        <span>Upload images</span>
                        <input
                          id="images"
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Info className="h-5 w-5" />
              <p>Your listing will be reviewed before being published</p>
            </div>

            <button
              type="submit"
              className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Submit Listing
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddListing;